import React from 'react'
import {
  Box,
  Flex,
  Text,
  Select,
  Button,
  Spinner,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'

import {
  FiBookOpen,
  FiUploadCloud,
  FiEye,
  FiFileText,
} from 'react-icons/fi'

import xhr from 'xhr'

export default function ContenidosProgramaticos() {
  const toast = useToast()

  /*
   * ==========================================================
   * ESTADOS GENERALES
   * ==========================================================
   */

  const [programTypes, setProgramTypes] =
    React.useState([])

  const [programs, setPrograms] =
    React.useState([])

  const [
    selectedProgramTypeId,
    setSelectedProgramTypeId,
  ] = React.useState('')

  const [
    selectedProgramId,
    setSelectedProgramId,
  ] = React.useState('')

  const [
    loadingProgramTypes,
    setLoadingProgramTypes,
  ] = React.useState(true)

  const [
    loadingPrograms,
    setLoadingPrograms,
  ] = React.useState(false)

  /*
   * ==========================================================
   * ARCHIVOS
   * ==========================================================
   */

  /*
   * Contenido programático
   * Se guarda en: course_content
   */
  const [
    courseContentFile,
    setCourseContentFile,
  ] = React.useState(null)

  const [
    uploadingCourseContent,
    setUploadingCourseContent,
  ] = React.useState(false)

  /*
   * Pensum
   * Se guarda en: pensum
   */
  const [
    pensumFile,
    setPensumFile,
  ] = React.useState(null)

  const [
    uploadingPensum,
    setUploadingPensum,
  ] = React.useState(false)

  /*
   * ==========================================================
   * REFERENCIAS INPUTS
   * ==========================================================
   */

  const courseContentInputRef =
    React.useRef(null)

  const pensumInputRef =
    React.useRef(null)

  /*
   * ==========================================================
   * MODAL PREVISUALIZACIÓN
   * ==========================================================
   */

  const [
    previewOpen,
    setPreviewOpen,
  ] = React.useState(false)

  const [
    previewUrl,
    setPreviewUrl,
  ] = React.useState('')

  const [
    previewTitle,
    setPreviewTitle,
  ] = React.useState('')

  /*
   * ==========================================================
   * HELPERS
   * ==========================================================
   */

  const getAttrs = (item) =>
    item?.attributes || item || {}

  const getRelationData = (relation) => {
    if (!relation) {
      return null
    }

    /*
     * Formato estándar Strapi:
     *
     * {
     *   data: {
     *     id,
     *     attributes
     *   }
     * }
     */
    if (relation?.data) {
      return {
        id: relation.data.id,
        ...(relation.data.attributes ||
          relation.data),
      }
    }

    /*
     * Por si transformer devuelve
     * directamente el objeto.
     */
    return relation
  }

  const normalizeList = (response) => {
    const payload =
      response?.data ?? response

    if (Array.isArray(payload)) {
      return payload
    }

    if (
      Array.isArray(payload?.data)
    ) {
      return payload.data
    }

    return []
  }

  /*
   * ==========================================================
   * LIMPIAR ARCHIVOS SELECCIONADOS
   * ==========================================================
   */

  const clearSelectedFiles = () => {
    setCourseContentFile(null)
    setPensumFile(null)

    if (
      courseContentInputRef.current
    ) {
      courseContentInputRef.current.value =
        ''
    }

    if (pensumInputRef.current) {
      pensumInputRef.current.value =
        ''
    }
  }

  /*
   * ==========================================================
   * CARGAR TIPOS DE PROGRAMA
   *
   * Excluimos "course".
   * ==========================================================
   */

  React.useEffect(() => {
    const loadProgramTypes =
      async () => {
        try {
          setLoadingProgramTypes(
            true
          )

          const response =
            await xhr(
              '/program-types?' +
              'filters[type][$ne]=course' +
              '&filters[active][$eq]=true' +
              '&sort[0]=name:asc' +
              '&pagination[pageSize]=200'
            )

          setProgramTypes(
            normalizeList(response)
          )
        } catch (error) {
          console.error(
            'Error cargando tipos de programa:',
            error
          )

          toast({
            title: 'Error',
            description:
              'No fue posible cargar los tipos de programa.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
        } finally {
          setLoadingProgramTypes(
            false
          )
        }
      }

    loadProgramTypes()
  }, [])

  /*
   * ==========================================================
   * TIPO DE PROGRAMA SELECCIONADO
   * ==========================================================
   */

  const selectedProgramType =
    React.useMemo(() => {
      return programTypes.find(
        (item) =>
          Number(item.id) ===
          Number(
            selectedProgramTypeId
          )
      )
    }, [
      programTypes,
      selectedProgramTypeId,
    ])

  const selectedProgramTypeAttrs =
    getAttrs(selectedProgramType)

  const selectedProgramTypeValue =
    selectedProgramTypeAttrs?.type ||
    ''

  /*
   * ==========================================================
   * CARGAR GRADOS O CARRERAS TÉCNICAS
   * ==========================================================
   */

  const loadPrograms =
    React.useCallback(
      async () => {
        if (
          !selectedProgramTypeId ||
          !selectedProgramTypeValue
        ) {
          setPrograms([])
          return
        }

        try {
          setLoadingPrograms(true)

          let endpoint = ''

          if (
            selectedProgramTypeValue ===
            'grade'
          ) {
            endpoint = '/grades'
          }

          if (
            selectedProgramTypeValue ===
            'technical_career'
          ) {
            endpoint =
              '/technical-careers'
          }

          if (!endpoint) {
            setPrograms([])
            return
          }

          /*
           * IMPORTANTE:
           *
           * Ahora poblamos ambos campos:
           *
           * pensum
           * course_content
           */
          const response =
            await xhr(
              `${endpoint}?` +
              `filters[program_type][id][$eq]=${selectedProgramTypeId}` +
              '&filters[active][$eq]=true' +
              '&populate[pensum]=*' +
              '&populate[course_content]=*' +
              '&sort[0]=name:asc' +
              '&pagination[pageSize]=300'
            )

          setPrograms(
            normalizeList(response)
          )
        } catch (error) {
          console.error(
            'Error cargando programas:',
            error
          )

          setPrograms([])

          toast({
            title: 'Error',
            description:
              'No fue posible cargar los programas.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
        } finally {
          setLoadingPrograms(
            false
          )
        }
      },
      [
        selectedProgramTypeId,
        selectedProgramTypeValue,
      ]
    )

  React.useEffect(() => {
    loadPrograms()
  }, [loadPrograms])

  /*
   * ==========================================================
   * PROGRAMA SELECCIONADO
   * ==========================================================
   */

  const selectedProgram =
    React.useMemo(() => {
      return programs.find(
        (item) =>
          Number(item.id) ===
          Number(selectedProgramId)
      )
    }, [
      programs,
      selectedProgramId,
    ])

  const selectedProgramAttrs =
    getAttrs(selectedProgram)

  /*
   * ==========================================================
   * CONTENIDO PROGRAMÁTICO
   *
   * Campo Strapi:
   * course_content
   * ==========================================================
   */

  const courseContent =
    getRelationData(
      selectedProgramAttrs?.course_content
    )

  const courseContentUrl =
    courseContent?.url || ''

  /*
   * ==========================================================
   * PENSUM
   *
   * Campo Strapi:
   * pensum
   * ==========================================================
   */

  const pensum =
    getRelationData(
      selectedProgramAttrs?.pensum
    )

  const pensumUrl =
    pensum?.url || ''

  /*
   * ==========================================================
   * CAMBIAR TIPO DE PROGRAMA
   * ==========================================================
   */

  const handleProgramTypeChange = (
    value
  ) => {
    setSelectedProgramTypeId(value)

    setSelectedProgramId('')

    setPrograms([])

    clearSelectedFiles()
  }

  /*
   * ==========================================================
   * CAMBIAR PROGRAMA
   * ==========================================================
   */

  const handleProgramChange = (
    value
  ) => {
    setSelectedProgramId(value)

    clearSelectedFiles()
  }

  /*
   * ==========================================================
   * VALIDAR PDF
   * ==========================================================
   */

  const validatePdf = (
    event,
    setFile,
    documentName
  ) => {
    const selectedFile =
      event.target.files?.[0] ||
      null

    if (!selectedFile) {
      setFile(null)
      return
    }

    /*
     * Validar extensión / MIME.
     */
    const isPdf =
      selectedFile.type ===
      'application/pdf' ||
      selectedFile.name
        .toLowerCase()
        .endsWith('.pdf')

    if (!isPdf) {
      toast({
        title:
          'Archivo no válido',
        description: `${documentName} debe ser un archivo PDF.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })

      event.target.value = ''

      setFile(null)

      return
    }

    /*
     * Máximo 20 MB.
     */
    const maxSize =
      20 * 1024 * 1024

    if (
      selectedFile.size >
      maxSize
    ) {
      toast({
        title:
          'Archivo demasiado grande',
        description:
          'El PDF no debe superar los 20 MB.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })

      event.target.value = ''

      setFile(null)

      return
    }

    setFile(selectedFile)
  }

  /*
   * ==========================================================
   * SUBIR DOCUMENTO
   *
   * Esta función sirve tanto para:
   *
   * course_content
   * pensum
   * ==========================================================
   */

  const uploadDocument = async ({
    file,
    fieldName,
    documentName,
    currentUrl,
    setFile,
    inputRef,
    setUploading,
  }) => {
    if (!selectedProgramId) {
      toast({
        title:
          'Selecciona un programa',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })

      return
    }

    if (!file) {
      toast({
        title:
          'Selecciona un PDF',
        description: `Debes seleccionar el archivo de ${documentName.toLowerCase()}.`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })

      return
    }

    try {
      setUploading(true)

      /*
       * ======================================================
       * 1. SUBIR ARCHIVO A MEDIA LIBRARY
       * ======================================================
       */

      const formData =
        new FormData()

      formData.append(
        'files',
        file
      )

      const uploadResponse =
        await xhr('/upload', {
          method: 'post',

          data: formData,

          headers: {
            'Content-Type':
              'multipart/form-data',
          },
        })

      const uploadPayload =
        uploadResponse?.data ??
        uploadResponse

      const uploadedFiles =
        Array.isArray(
          uploadPayload
        )
          ? uploadPayload
          : uploadPayload?.data ||
          []

      const uploadedFile =
        Array.isArray(
          uploadedFiles
        )
          ? uploadedFiles[0]
          : uploadedFiles

      const uploadedFileId =
        uploadedFile?.id

      if (!uploadedFileId) {
        throw new Error(
          'Strapi no devolvió el ID del archivo.'
        )
      }

      /*
       * ======================================================
       * 2. DETERMINAR ENDPOINT
       * ======================================================
       */

      const endpoint =
        selectedProgramTypeValue ===
          'technical_career'
          ? '/technical-careers'
          : '/grades'

      /*
       * ======================================================
       * 3. ASOCIAR ARCHIVO AL CAMPO CORRECTO
       *
       * fieldName puede ser:
       *
       * course_content
       * pensum
       * ======================================================
       */

      await xhr.put(
        `${endpoint}/${selectedProgramId}`,
        {
          data: {
            [fieldName]:
              uploadedFileId,
          },
        }
      )

      /*
       * ======================================================
       * 4. MENSAJE
       * ======================================================
       */

      toast({
        title: currentUrl
          ? `${documentName} actualizado`
          : `${documentName} cargado`,

        description:
          'El PDF fue guardado correctamente.',

        status: 'success',

        position: 'bottom',

        duration: 3000,

        isClosable: true,
      })

      /*
       * Limpiar archivo.
       */

      setFile(null)

      if (inputRef.current) {
        inputRef.current.value =
          ''
      }

      /*
       * Volvemos a consultar el programa
       * para obtener la nueva relación.
       */

      await loadPrograms()
    } catch (error) {
      console.error(
        `Error subiendo ${documentName}:`,
        error
      )

      const msg =
        error?.response?.data
          ?.error?.message ||
        error?.response?.data
          ?.message ||
        error?.message ||
        `No se pudo cargar ${documentName.toLowerCase()}.`

      toast({
        title:
          'Error al subir el PDF',

        description: msg,

        status: 'error',

        duration: 5000,

        isClosable: true,
      })
    } finally {
      setUploading(false)
    }
  }

  /*
   * ==========================================================
   * SUBIR CONTENIDO PROGRAMÁTICO
   * ==========================================================
   */

  const uploadCourseContent =
    () => {
      return uploadDocument({
        file: courseContentFile,

        fieldName:
          'course_content',

        documentName:
          'Contenido programático',

        currentUrl:
          courseContentUrl,

        setFile:
          setCourseContentFile,

        inputRef:
          courseContentInputRef,

        setUploading:
          setUploadingCourseContent,
      })
    }

  /*
   * ==========================================================
   * SUBIR PENSUM
   * ==========================================================
   */

  const uploadPensum = () => {
    return uploadDocument({
      file: pensumFile,

      fieldName: 'pensum',

      documentName: 'Pensum',

      currentUrl: pensumUrl,

      setFile:
        setPensumFile,

      inputRef:
        pensumInputRef,

      setUploading:
        setUploadingPensum,
    })
  }

  /*
   * ==========================================================
   * ABRIR PREVISUALIZACIÓN
   * ==========================================================
   */

  const openPreview = (
    url,
    title
  ) => {
    if (!url) {
      return
    }

    setPreviewUrl(url)
    setPreviewTitle(title)
    setPreviewOpen(true)
  }

  const closePreview = () => {
    setPreviewOpen(false)
    setPreviewUrl('')
    setPreviewTitle('')
  }

  /*
   * ==========================================================
   * TEXTO DINÁMICO
   * ==========================================================
   */

  const programLabel =
    selectedProgramTypeValue ===
      'technical_career'
      ? 'Carrera técnica'
      : 'Grado'

  /*
   * ==========================================================
   * COMPONENTE INTERNO PARA CADA DOCUMENTO
   * ==========================================================
   */

  const renderDocumentManager = ({
    title,
    description,
    document,
    url,
    file,
    inputRef,
    onFileChange,
    onUpload,
    uploading,
  }) => {
    const available =
      Boolean(url)

    return (
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        overflow="hidden"
        bg="white"
      >
        {/* Encabezado */}
        <Flex
          px={5}
          py={4}
          bg="gray.50"
          borderBottomWidth="1px"
          justify="space-between"
          align="center"
          gap={4}
          flexWrap="wrap"
        >
          <Flex
            align="center"
            gap={3}
          >
            <Box
              as={FiFileText}
              boxSize={5}
              color="#574feb"
            />

            <Box>
              <Text
                fontWeight="bold"
                color="gray.800"
              >
                {title}
              </Text>

              <Text
                fontSize="xs"
                color="gray.500"
                mt={0.5}
              >
                {description}
              </Text>
            </Box>
          </Flex>

          {available ? (
            <Badge
              colorScheme="green"
              px={3}
              py={1}
              borderRadius="full"
            >
              PDF disponible
            </Badge>
          ) : (
            <Badge
              colorScheme="orange"
              px={3}
              py={1}
              borderRadius="full"
            >
              Sin documento
            </Badge>
          )}
        </Flex>

        <Box p={5}>
          {/* Documento existente */}
          {available && (
            <Box
              bg="green.50"
              borderWidth="1px"
              borderColor="green.100"
              borderRadius="lg"
              p={4}
              mb={5}
            >
              <Flex
                justify="space-between"
                align="center"
                gap={4}
                flexWrap="wrap"
              >
                <Flex
                  align="center"
                  gap={3}
                >
                  <Box
                    as={FiFileText}
                    color="green.600"
                    boxSize={6}
                  />

                  <Box>
                    <Text
                      fontWeight="semibold"
                      color="green.800"
                    >
                      {title} disponible
                    </Text>

                    <Text
                      fontSize="xs"
                      color="green.600"
                      mt={1}
                    >
                      {document?.name ||
                        'Documento PDF'}
                    </Text>
                  </Box>
                </Flex>

                <Flex
                  gap={2}
                  flexWrap="wrap"
                >
                  <Button
                    size="sm"
                    leftIcon={
                      <FiEye />
                    }
                    colorScheme="green"
                    variant="outline"
                    onClick={() =>
                      openPreview(
                        url,
                        title
                      )
                    }
                  >
                    Ver PDF
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      window.open(
                        url,
                        '_blank'
                      )
                    }
                  >
                    Abrir en otra pestaña
                  </Button>
                </Flex>
              </Flex>
            </Box>
          )}

          {/* Sin documento */}
          {!available && (
            <Box
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.100"
              borderRadius="lg"
              p={4}
              mb={5}
            >
              <Text
                fontWeight="semibold"
                color="orange.800"
              >
                Este programa todavía no
                tiene {title.toLowerCase()}.
              </Text>

              <Text
                fontSize="sm"
                color="orange.700"
                mt={1}
              >
                Selecciona un archivo PDF
                para cargarlo.
              </Text>
            </Box>
          )}

          {/* Upload */}
          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor="gray.200"
            p={5}
          >
            <FormControl>
              <FormLabel>
                {available
                  ? `Reemplazar ${title.toLowerCase()}`
                  : `Subir ${title.toLowerCase()}`}
              </FormLabel>

              <Input
                type="file"
                accept="application/pdf,.pdf"
                ref={inputRef}
                onChange={
                  onFileChange
                }
                pt={1}
              />

              <Text
                fontSize="xs"
                color="gray.500"
                mt={2}
              >
                Solo archivos PDF. Tamaño
                máximo recomendado: 20 MB.
              </Text>
            </FormControl>

            {file && (
              <Box
                mt={4}
                bg="purple.50"
                borderRadius="md"
                p={3}
              >
                <Text
                  fontSize="sm"
                  fontWeight="500"
                >
                  Archivo seleccionado:
                </Text>

                <Text
                  fontSize="sm"
                  color="gray.600"
                  mt={1}
                >
                  {file.name}
                </Text>
              </Box>
            )}

            <Flex
              justify="flex-end"
              mt={5}
            >
              <Button
                bg="#574feb"
                color="white"
                _hover={{
                  bg: '#4840d9',
                }}
                leftIcon={
                  <FiUploadCloud />
                }
                onClick={onUpload}
                isLoading={uploading}
                loadingText="Subiendo..."
                isDisabled={!file}
              >
                {available
                  ? 'Reemplazar PDF'
                  : 'Subir PDF'}
              </Button>
            </Flex>
          </Box>
        </Box>
      </Box>
    )
  }

  /*
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  return (
    <Box
      p={5}
      maxW="100%"
    >
      {/* ======================================================
          ENCABEZADO
          ====================================================== */}

      <Box
        bg="white"
        borderWidth="1px"
        borderColor="gray.100"
        borderRadius="xl"
        boxShadow="sm"
        overflow="hidden"
      >
        <Flex
          bg="#574feb"
          color="white"
          px={6}
          py={4}
          align="center"
          gap={3}
        >
          <Box
            as={FiBookOpen}
            boxSize={6}
          />

          <Box>
            <Text
              fontSize="xl"
              fontWeight="bold"
            >
              Pensum y Contenidos
              Programáticos
            </Text>

            <Text
              fontSize="sm"
              opacity={0.9}
            >
              Consulta, carga y actualiza
              los documentos académicos
              de cada programa.
            </Text>
          </Box>
        </Flex>

        {/* ====================================================
            FILTROS
            ==================================================== */}

        <Box p={6}>
          <Flex
            gap={5}
            direction={{
              base: 'column',
              md: 'row',
            }}
          >
            {/* Tipo programa */}
            <FormControl>
              <FormLabel>
                Tipo de programa
              </FormLabel>

              {loadingProgramTypes ? (
                <Flex
                  align="center"
                  gap={2}
                  minH="40px"
                >
                  <Spinner
                    size="sm"
                  />

                  <Text fontSize="sm">
                    Cargando...
                  </Text>
                </Flex>
              ) : (
                <Select
                  placeholder="Selecciona un tipo de programa"
                  value={
                    selectedProgramTypeId
                  }
                  onChange={(e) =>
                    handleProgramTypeChange(
                      e.target.value
                    )
                  }
                >
                  {programTypes.map(
                    (item) => {
                      const attrs =
                        getAttrs(item)

                      return (
                        <option
                          key={
                            item.id
                          }
                          value={
                            item.id
                          }
                        >
                          {
                            attrs.name
                          }
                        </option>
                      )
                    }
                  )}
                </Select>
              )}
            </FormControl>

            {/* Grado / carrera */}
            <FormControl>
              <FormLabel>
                {programLabel}
              </FormLabel>

              <Select
                placeholder={
                  !selectedProgramTypeId
                    ? 'Primero selecciona un tipo de programa'
                    : `Selecciona ${programLabel.toLowerCase()}`
                }
                value={
                  selectedProgramId
                }
                onChange={(e) =>
                  handleProgramChange(
                    e.target.value
                  )
                }
                isDisabled={
                  !selectedProgramTypeId ||
                  loadingPrograms
                }
              >
                {programs.map(
                  (item) => {
                    const attrs =
                      getAttrs(item)

                    return (
                      <option
                        key={item.id}
                        value={
                          item.id
                        }
                      >
                        {
                          attrs.name
                        }
                      </option>
                    )
                  }
                )}
              </Select>

              {loadingPrograms && (
                <Flex
                  mt={2}
                  gap={2}
                  align="center"
                >
                  <Spinner
                    size="xs"
                  />

                  <Text
                    fontSize="xs"
                    color="gray.500"
                  >
                    Cargando programas...
                  </Text>
                </Flex>
              )}
            </FormControl>
          </Flex>
        </Box>
      </Box>

      {/* ======================================================
          ESTADO INICIAL
          ====================================================== */}

      {!selectedProgramId && (
        <Box
          mt={6}
          borderWidth="2px"
          borderStyle="dashed"
          borderColor="gray.200"
          borderRadius="xl"
          bg="gray.50"
          py={14}
          px={6}
          textAlign="center"
        >
          <Box
            as={FiFileText}
            boxSize={12}
            color="#574feb"
            mx="auto"
            mb={4}
          />

          <Text
            fontWeight="semibold"
            fontSize="lg"
          >
            Selecciona un programa
          </Text>

          <Text
            color="gray.500"
            fontSize="sm"
            mt={1}
          >
            Selecciona el tipo de programa
            y posteriormente el grado o
            carrera técnica.
          </Text>
        </Box>
      )}

      {/* ======================================================
          PROGRAMA SELECCIONADO
          ====================================================== */}

      {selectedProgramId &&
        selectedProgram && (
          <Box mt={6}>
            {/* Información programa */}

            <Box
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              borderRadius="xl"
              boxShadow="sm"
              px={6}
              py={4}
              mb={5}
            >
              <Text
                fontSize="xs"
                color="gray.500"
                textTransform="uppercase"
                fontWeight="bold"
              >
                {
                  selectedProgramTypeAttrs?.name
                }
              </Text>

              <Text
                fontSize="xl"
                fontWeight="bold"
                color="gray.800"
                mt={1}
              >
                {
                  selectedProgramAttrs?.name
                }
              </Text>
            </Box>

            {/* Documentos */}

            <Flex
              direction="column"
              gap={5}
            >
              {/* ==============================================
                  CONTENIDO PROGRAMÁTICO
                  ============================================== */}

              {renderDocumentManager({
                title:
                  'Contenido programático',

                description:
                  'Documento con el desarrollo y estructura académica del programa.',

                document:
                  courseContent,

                url:
                  courseContentUrl,

                file:
                  courseContentFile,

                inputRef:
                  courseContentInputRef,

                onFileChange: (
                  event
                ) =>
                  validatePdf(
                    event,
                    setCourseContentFile,
                    'El contenido programático'
                  ),

                onUpload:
                  uploadCourseContent,

                uploading:
                  uploadingCourseContent,
              })}

              {/* ==============================================
                  PENSUM
                  ============================================== */}

              {renderDocumentManager({
                title: 'Pensum',

                description:
                  'Documento correspondiente al pensum académico del programa.',

                document: pensum,

                url: pensumUrl,

                file: pensumFile,

                inputRef:
                  pensumInputRef,

                onFileChange: (
                  event
                ) =>
                  validatePdf(
                    event,
                    setPensumFile,
                    'El pensum'
                  ),

                onUpload:
                  uploadPensum,

                uploading:
                  uploadingPensum,
              })}
            </Flex>
          </Box>
        )}

      {/* ======================================================
          MODAL PDF
          ====================================================== */}

      <Modal
        isOpen={previewOpen}
        onClose={closePreview}
        size="6xl"
      >
        <ModalOverlay />

        <ModalContent
          height="90vh"
          maxH="90vh"
        >
          <ModalHeader>
            {previewTitle}

            {selectedProgramAttrs?.name
              ? ` - ${selectedProgramAttrs.name}`
              : ''}
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody p={0}>
            {previewUrl && (
              <iframe
                src={previewUrl}
                title={
                  previewTitle ||
                  'Documento PDF'
                }
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  minHeight:
                    '75vh',
                }}
              />
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={
                closePreview
              }
            >
              Cerrar
            </Button>

            <Button
              bg="#574feb"
              color="white"
              _hover={{
                bg: '#4840d9',
              }}
              onClick={() =>
                window.open(
                  previewUrl,
                  '_blank'
                )
              }
              isDisabled={
                !previewUrl
              }
            >
              Abrir PDF
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}